import { useState, useEffect } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { FileSpreadsheet, RefreshCw, Users, Download, History, Upload as UploadIcon, Upload, ArrowRight, ArrowLeft, AlertTriangle, CheckCircle2 } from "lucide-react";

export default function LeagueImport() {
  const { league, reload } = useLeague();
  const [history, setHistory] = useState([]);
  const [refreshCounter, setRefreshCounter] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${league.league_id}/import-history?limit=30`);
        setHistory(r.data.events || []);
      } catch (e) { /* silenzioso */ }
    })();
  }, [league.league_id, refreshCounter]);

  const notifyDone = () => { setRefreshCounter(x => x + 1); if (reload) reload(); };
  const downloadTemplate = async () => {
    try {
      const r = await api.get(`/leagues/${league.league_id}/imports/template.xlsx`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `template_${(league.name || "lega").replace(/\s+/g,"_")}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Template scaricato");
    } catch (e) { toast.error("Errore download template"); }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Header */}
      <div>
        <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold">📤 Caricamento dati</div>
        <h1 className="text-3xl font-black tracking-tight mt-1">Importa dati esterni</h1>
        <p className="text-slate-400 text-sm mt-1">
          3 tipi di file. Ogni nuovo caricamento aggiorna il precedente. Trovi lo storico in fondo.
        </p>
      </div>

      {/* SEZIONE 1 — FantaQuotazioni */}
      <ImportCard
        icon="📊" color="cyan" title="1️⃣ FantaQuotazioni"
        subtitle="Listone globale (file Quotazioni Fantacalcio.it .xlsx)"
        desc="Aggiorna: squadre reali dei giocatori · QA/QI/FVM · nuovi giocatori nel rooster globale · foglio Ceduti → LISTED_CEDUTO. NON modifica le proprietà dei fantaclub."
        accept=".xlsx,.xls">
        {(file, setLoading, done) => (
          <button
            data-testid="fantaquotazioni-upload-btn"
            disabled={!file}
            onClick={async () => {
              setLoading(true);
              try {
                const fd = new FormData(); fd.append("file", file);
                const r = await api.post(`/leagues/${league.league_id}/imports/quotazioni?mode=update_current&preview=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                const upd = r.data.updated || 0;
                const trans = r.data.transfers_detected || 0;
                const rev = r.data.revalue?.significant_changes || 0;
                toast.success(`✅ FantaQuotazioni: ${upd} giocatori aggiornati · ${trans} trasferimenti reali · ${rev} variazioni significative`);
                done(); notifyDone();
              } catch (e) { toast.error(formatApiError(e)); }
              finally { setLoading(false); }
            }}
            className="px-6 py-2.5 rounded-2xl bg-cyan-500/20 border border-cyan-500/50 text-cyan-200 font-black text-sm hover:bg-cyan-500/30 disabled:opacity-40">
            📊 Carica FantaQuotazioni
          </button>
        )}
      </ImportCard>

      {/* SEZIONE 2 — Rose */}
      <ImportCard
        icon="👥" color="emerald" title="2️⃣ Rose"
        subtitle="Lista rose Fantacalcio.it (file .csv Rose la-lega-dei-magnati)"
        desc="Aggiorna solo la PROPRIETÀ (chi ha chi). Scambi e vendite già in DB restano. I giocatori non presenti nel file vengono marchiati 🔎 'Da verificare' (colorati). Usa solo per Parte 1 e Parte 3 del mercato di riparazione."
        accept=".csv"
        extraActions={(
          <div className="flex gap-2">
            <button
              data-testid="run-diagnostic-btn"
              onClick={async () => {
                try {
                  const r = await api.get(`/leagues/${league.league_id}/imports/diagnostic`);
                  const d = r.data;
                  toast.info(`🩺 DB: ${d.players_total} giocatori (${d.players_with_fanta_id} con fanta_id, ${d.players_without_fanta_id} senza) · Rooster: ${d.rooster_total}`, { duration: 12000 });
                  console.info("[Diagnostic]", d);
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="px-3 py-1.5 rounded-full bg-slate-800 border border-slate-600 text-slate-200 text-xs font-bold hover:bg-slate-700 flex items-center gap-1">
              🩺 Diagnosi
            </button>
            <button
              data-testid="auto-link-fids-btn"
              onClick={async () => {
                if (!confirm("🔧 Fuzzy-collegherò tutti i giocatori DB senza fanta_id ai record del rooster (soglia 70%). Preview prima?")) return;
                const preview = window.confirm("Vuoi vedere prima l'anteprima (dry run)? OK = anteprima, Annulla = applica direttamente");
                try {
                  const r = await api.post(`/leagues/${league.league_id}/imports/auto-link-fanta-ids?dry_run=${preview}`);
                  const d = r.data;
                  if (d.dry_run) {
                    toast.info(`🔍 Preview: ${d.proposals_count} collegamenti proposti su ${d.unmapped_total} unmapped`, { duration: 10000 });
                    console.info("[Auto-Link Preview]", d);
                  } else {
                    toast.success(`✅ ${d.linked} giocatori auto-collegati (${d.unmapped_after} ancora senza fanta_id)`, { duration: 10000 });
                    console.info("[Auto-Link Apply]", d);
                  }
                } catch (e) { toast.error(formatApiError(e)); }
              }}
              className="px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/50 text-cyan-200 text-xs font-bold hover:bg-cyan-500/25 flex items-center gap-1">
              🔧 Auto-collega fanta_id
            </button>
          </div>
        )}>
        {(file, setLoading, done, loading) => (
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="rose-preview-btn"
              disabled={!file}
              onClick={async () => {
                setLoading(true);
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const r = await api.post(`/leagues/${league.league_id}/imports/rose-fantacalcio?preview=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const s = r.data.summary || {};
                  toast.success(`🔍 Anteprima: ${r.data.total_teams_matched}/${r.data.total_teams_in_file} team · ${s.confirmed} confermati · ${s.transferred_internal} trasferiti · ${s.missing_from_file} da verificare`);
                  // Iter 77: diff dettagliato
                  const t = r.data.preview_transfers || [];
                  const sr = r.data.preview_status_restores || [];
                  const rt = r.data.preview_real_team_changes || [];
                  if (t.length > 0) {
                    const txt = t.slice(0, 5).map(x => `${x.player_name}: ${x.from_team_name} → ${x.to_team_name}`).join(" · ");
                    setTimeout(() => toast.info(`🔄 Trasferimenti (${t.length}): ${txt}${t.length > 5 ? " …" : ""}`, { duration: 10000 }), 400);
                  }
                  if (sr.length > 0) {
                    const txt = sr.slice(0, 5).map(x => `${x.player_name} → ${x.to_team_name}`).join(" · ");
                    setTimeout(() => toast.info(`♻️ Rientrano in rosa (${sr.length}): ${txt}${sr.length > 5 ? " …" : ""}`, { duration: 10000 }), 700);
                  }
                  if (rt.length > 0) {
                    const txt = rt.slice(0, 5).map(x => `${x.player_name}: ${x.from_real_team || "?"} → ${x.to_real_team}`).join(" · ");
                    setTimeout(() => toast.info(`⚽ Cambi Serie A (${rt.length}): ${txt}${rt.length > 5 ? " …" : ""}`, { duration: 10000 }), 1000);
                  }
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setLoading(false); }
              }}
              className="px-4 py-2.5 rounded-2xl bg-slate-800 border border-slate-700 text-slate-200 font-bold text-sm hover:bg-slate-700 disabled:opacity-40">
              🔍 Anteprima
            </button>
            <button
              data-testid="rose-apply-btn"
              disabled={!file}
              onClick={async () => {
                if (!confirm("Applicare le proprietà dal file rose Fantacalcio.it? Verrà creato uno snapshot pre-import.")) return;
                setLoading(true);
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const r = await api.post(`/leagues/${league.league_id}/imports/rose-fantacalcio?preview=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const trans = r.data.applied_transfers || 0;
                  const restored = r.data.status_restored || 0;
                  const created = r.data.created_players || 0;
                  const unresolved = r.data.unresolved_players_count || 0;
                  if (trans === 0 && restored === 0 && created === 0) {
                    toast.warning(`⚠️ Nessuna modifica applicata. ${unresolved > 0 ? `${unresolved} giocatori non riconosciuti nel DB (vedi console).` : "Le rose sono già allineate al file."}`, { duration: 12000 });
                    if (unresolved > 0 && r.data.unresolved_players_details) {
                      console.warn("[Import Rose] Giocatori non risolti:", r.data.unresolved_players_details);
                    }
                  } else {
                    toast.success(`✅ Rose: ${created} creati · ${trans} trasferimenti · ${restored} rientrati${unresolved > 0 ? ` · ${unresolved} non risolti` : ""} · snapshot ${r.data.snapshot_id}`, { duration: 12000 });
                  }
                  done(); notifyDone();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setLoading(false); }
              }}
              className="px-6 py-2.5 rounded-2xl bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 font-black text-sm hover:bg-emerald-500/30 disabled:opacity-40">
              ✅ Carica Rose
            </button>
            <button
              data-testid="rose-smart-apply-btn"
              disabled={!file || loading}
              onClick={async () => {
                if (!confirm("🤖 Smart Apply: Claude Sonnet 5 proverà a mappare i nomi non riconosciuti (es. 'Martinez L.' → 'Lautaro Martinez') e poi applicherà i trasferimenti. Può richiedere 15-30 secondi.")) return;
                setLoading(true);
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const r = await api.post(`/leagues/${league.league_id}/imports/rose-fantacalcio/smart-apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const d = r.data;
                  const parts = [
                    `${d.applied_transfers || 0} trasferimenti`,
                    `${d.status_restored || 0} rientrati`,
                    `AI: ${d.ai_resolved || 0} risolti · ${d.ai_skipped || 0} skip`,
                  ];
                  if (d.unresolved_final > 0) parts.push(`${d.unresolved_final} ancora non risolti`);
                  toast.success(`🤖 Smart Apply: ${parts.join(" · ")}`, { duration: 12000 });
                  if (d.unresolved_final > 0 && d.unresolved_final_details) {
                    console.warn("[Smart Apply] Ancora non risolti:", d.unresolved_final_details);
                  }
                  if (d.ai_decisions_sample) {
                    console.info("[Smart Apply] Decisioni AI:", d.ai_decisions_sample);
                  }
                  done(); notifyDone();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setLoading(false); }
              }}
              title="Usa Claude Sonnet 5 per mappare i nomi non riconosciuti prima di applicare"
              className="px-6 py-2.5 rounded-2xl bg-fuchsia-500/20 border border-fuchsia-500/50 text-fuchsia-200 font-black text-sm hover:bg-fuchsia-500/30 disabled:opacity-40">
              🤖 Smart Apply
            </button>
          </div>
        )}
      </ImportCard>

      {/* SEZIONE 3 — AggiornaExcel */}
      <ImportCard
        icon="📅" color="amber" title="3️⃣ AggiornaExcel"
        subtitle="Post-Calendario admin (controllo + correzioni manuali)"
        desc="Template ufficiale MFB. Colonna 'Costo d'acquisto' AUTORITATIVA. Serve per aggiungere manualmente correzioni e fare un check incrociato con FantaQuotazioni e Rose."
        accept=".xlsx,.xls"
        extraActions={(
          <button
            onClick={downloadTemplate}
            data-testid="download-template-btn"
            className="px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-xs font-bold hover:bg-amber-500/25 flex items-center gap-1">
            <Download size={12}/> Scarica template
          </button>
        )}>
        {(file, setLoading, done, loading) => (
          <div className="flex gap-2">
            <button
              data-testid="postcal-preview-btn"
              disabled={!file}
              onClick={async () => {
                setLoading(true);
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const r = await api.post(`/leagues/${league.league_id}/imports/post-calendario?preview=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const s = r.data.summary || {};
                  const teamUps = s.team_updates || 0;
                  const bd = r.data.preview_budget_deltas || [];
                  toast.success(`🔍 Anteprima: ${s.confirmed} confermati · ${s.cost_updates} costi da aggiornare${teamUps ? ` · ${teamUps} cambi squadra` : ""} · ${s.missing_from_sheet} mancanti`);
                  if (bd.length > 0) {
                    const dstr = bd.map(d => `${d.team_name}: ${d.delta > 0 ? '+' : ''}${d.delta}`).join(" · ");
                    setTimeout(() => toast.info(`💰 Budget delta preview: ${dstr}`, { duration: 8000 }), 400);
                  }
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setLoading(false); }
              }}
              className="px-4 py-2.5 rounded-2xl bg-slate-800 border border-slate-700 text-slate-200 font-bold text-sm hover:bg-slate-700 disabled:opacity-40">
              🔍 Anteprima
            </button>
            <button
              data-testid="postcal-apply-btn"
              disabled={!file}
              onClick={async () => {
                if (!confirm("La colonna 'Costo d\'acquisto' sovrascriverà i prezzi_pagato. Procedere?")) return;
                setLoading(true);
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const r = await api.post(`/leagues/${league.league_id}/imports/post-calendario?preview=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const teamUps = r.data.applied_team_updates || 0;
                  const budgetUps = r.data.applied_budget_updates || 0;
                  const statsBudget = r.data.stats_budget_updates_count || 0;
                  toast.success(`✅ AggiornaExcel: ${r.data.applied_updates} aggiornamenti${teamUps ? ` · ${teamUps} cambi squadra` : ""}${budgetUps ? ` · budget delta ${budgetUps}` : ""}${statsBudget ? ` · 💰 crediti residui aggiornati per ${statsBudget} team` : ""}`, { duration: 12000 });
                  done(); notifyDone();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setLoading(false); }
              }}
              className="px-6 py-2.5 rounded-2xl bg-amber-500/20 border border-amber-500/50 text-amber-200 font-black text-sm hover:bg-amber-500/30 disabled:opacity-40">
              ✅ Carica AggiornaExcel
            </button>
            <button
              data-testid="postcal-crediti-only-btn"
              disabled={!file}
              onClick={async () => {
                if (!window.confirm("🔄 Applicare SOLO i crediti residui dal foglio Statistiche? NON tocca rose, prezzi o giocatori — sovrascrive solo team.budget.")) return;
                setLoading(true);
                try {
                  const fd = new FormData(); fd.append("file", file);
                  const r = await api.post(`/leagues/${league.league_id}/imports/apply-statistiche-only`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                  const d = r.data;
                  const updates = d.updates || [];
                  const changed = updates.filter(u => Number(u.old_budget) !== Number(u.new_budget));
                  const unchanged = updates.filter(u => Number(u.old_budget) === Number(u.new_budget));
                  // Fetch team lega per capire quali NON sono nel foglio
                  let missingTeamsMsg = "";
                  try {
                    const rt = await api.get(`/leagues/${league.league_id}/teams`);
                    const allTeams = rt.data || [];
                    const updatedIds = new Set(updates.map(u => u.team_id));
                    const notInSheet = allTeams.filter(t => !updatedIds.has(t.team_id));
                    if (notInSheet.length > 0) {
                      missingTeamsMsg = `\n\n⚠️ TEAM ASSENTI dal foglio Statistiche (budget INVARIATO):\n` +
                                        notInSheet.map(t => `  • ${t.name} (attuale: ${t.budget} cr)`).join("\n") +
                                        `\n\n💡 Aggiungi questi team al foglio Excel nella sezione Statistiche (col 9 nome, col 13 crediti) e ricarica.`;
                    }
                  } catch (_) {}
                  const lines = updates.map(u => {
                    const eq = Number(u.old_budget) === Number(u.new_budget);
                    return `${eq ? "= " : "≠ "}${u.team_name}: ${u.old_budget} → ${u.new_budget} cr${eq ? " (invariato)" : ""}`;
                  });
                  const msg = `💰 CREDITI RESIDUI\n\n` +
                              `Team aggiornati: ${changed.length} · invariati: ${unchanged.length} · totali nel foglio: ${updates.length}\n\n` +
                              lines.join("\n") + missingTeamsMsg;
                  window.alert(msg);
                  toast.success(`💰 ${changed.length} team aggiornati · ${unchanged.length} invariati${missingTeamsMsg ? " · ⚠️ alcuni team mancano dal foglio" : ""}`, { duration: 10000 });
                  console.info("[Statistiche Only]", d.updates);
                  done(); notifyDone();
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setLoading(false); }
              }}
              title="Applica solo i crediti residui dal foglio Statistiche"
              className="px-4 py-2.5 rounded-2xl bg-emerald-500/20 border border-emerald-500/50 text-emerald-200 font-black text-xs hover:bg-emerald-500/30 disabled:opacity-40">
              💰 Solo Crediti
            </button>
            <button
              onClick={async () => {
                if (!window.confirm("Rollback dell'ultimo AggiornaExcel? Ripristina prezzi, squadre e budget allo stato pre-import.")) return;
                setLoading(true);
                try {
                  const r = await api.post(`/leagues/${league.league_id}/imports/post-calendario/undo`, {});
                  toast.success(`⏪ Rollback OK: ${r.data.restored_players} giocatori · ${r.data.restored_teams} budget squadre ripristinati`);
                } catch (e) { toast.error(formatApiError(e)); }
                finally { setLoading(false); }
              }}
              disabled={loading}
              data-testid="post-cal-undo-btn"
              className="px-4 py-2 rounded-full bg-rose-500/10 border border-rose-500/40 text-rose-300 font-bold text-xs hover:bg-rose-500/20 disabled:opacity-40 ml-auto">
              ⏪ Annulla ultimo AggiornaExcel
            </button>
          </div>
        )}
      </ImportCard>

      {/* STORICO caricamenti */}
      <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-5" data-testid="import-history-panel">
        <div className="flex items-center gap-2 mb-3">
          <History size={16} className="text-slate-500"/>
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-300">Storico caricamenti</h3>
          <span className="text-xs text-slate-500">({history.length})</span>
        </div>
        {history.length === 0 ? (
          <div className="text-xs text-slate-500 py-6 text-center italic">Nessun caricamento ancora effettuato.</div>
        ) : (
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {history.map((e, i) => (
              <div key={i} className="flex items-center justify-between text-xs bg-slate-950/60 rounded-xl px-3 py-2 border border-slate-800/60" data-testid={`history-row-${i}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-base">{e.icon}</span>
                  <div className="min-w-0">
                    <div className="font-bold text-slate-200">{e.label}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {e.action === "import_quotazioni_official" && `${e.summary.updated || 0} agg · ${e.summary.rooster_updated || 0} rooster · ${e.summary.transfers_detected || 0} transf`}
                      {e.action === "import_rose_fantacalcio" && `${e.summary.rose_summary?.confirmed || 0} confermati · ${e.summary.applied_transfers || 0} trasf applicati`}
                      {e.action === "import_post_calendario" && `${e.summary.applied || 0} aggiornamenti costo`}
                    </div>
                  </div>
                </div>
                <div className="text-[10px] text-slate-500 font-mono whitespace-nowrap">
                  {e.at ? new Date(e.at).toLocaleString("it-IT") : "—"}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ⚠️ DANGER ZONE — Reset totale stagione */}
      <PlayersListInline leagueId={league.league_id} refreshCounter={refreshCounter}/>

      <div className="rounded-3xl border-2 border-rose-500/50 bg-gradient-to-br from-rose-950/40 to-slate-950 p-5" data-testid="danger-zone">
        <div className="flex items-start gap-3 mb-3">
          <div className="text-3xl">⚠️</div>
          <div className="flex-1">
            <h3 className="text-lg font-black text-rose-200 uppercase tracking-wider">Danger Zone — Reset Stagione Corrente</h3>
            <p className="text-xs text-slate-300 mt-1 leading-relaxed">
              Cancella <strong>tutti i giocatori</strong> della lega, le <strong>operazioni</strong>, gli <strong>snapshot correnti</strong> e il <strong>rooster Serie A corrente</strong>. Reimposta i <strong>budget</strong> dei team al valore iniziale.
              <br/>
              <strong className="text-emerald-300">RESTA INTATTO</strong>: nomi squadre, logo, albo d&apos;oro, storico stagioni passate, magnati_rooster (carriere passate), listoni storici, utenti.
              <br/>
              Dopo il reset carichi i 3 file (FantaQuotazioni → Rose CSV → AggiornaExcel) e riparti da zero senza dati "sporchi".
            </p>
          </div>
        </div>
        <button
          data-testid="reset-current-season-btn"
          onClick={async () => {
            const c1 = window.confirm("⚠️ Sei sicuro di voler CANCELLARE tutti i giocatori + rooster corrente + operazioni della stagione? Solo l'owner può farlo. Azione IRREVERSIBILE (ma verrà creato uno snapshot di sicurezza).");
            if (!c1) return;
            const c2 = window.prompt("Conferma finale: scrivi esattamente RESET (in maiuscolo)");
            if (c2 !== "RESET") { toast.info("Reset annullato"); return; }
            try {
              const r = await api.post(`/leagues/${league.league_id}/imports/reset-current-season?confirm=RESET_STAGIONE_CORRENTE`);
              const d = r.data;
              toast.success(`🗑️ Reset OK: ${d.players_removed} giocatori · ${d.operations_removed} op · ${d.rooster_removed} rooster · budget → ${d.teams_reset_to_budget} cr. Snapshot safety: ${d.safety_snapshot_id}`, { duration: 20000 });
              console.info("[Reset Stagione]", d);
              notifyDone();
            } catch (e) { toast.error(formatApiError(e), { duration: 12000 }); }
          }}
          className="px-5 py-3 rounded-2xl bg-rose-600 hover:bg-rose-500 text-white font-black text-sm uppercase tracking-wider border-2 border-rose-400 shadow-lg shadow-rose-500/30">
          🗑️ Reset Stagione Corrente
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PlayersListInline — sezione debug per vedere tutti i giocatori DB
// ═══════════════════════════════════════════════════════════════════
function PlayersListInline({ leagueId, refreshCounter }) {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [rp, rt] = await Promise.all([
        api.get(`/leagues/${leagueId}/players`),
        api.get(`/leagues/${leagueId}/teams`),
      ]);
      setPlayers(rp.data || []);
      setTeams(rt.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open, refreshCounter]); // eslint-disable-line

  const teamName = (tid) => (teams.find(t => t.team_id === tid)?.name) || "(svincolato)";
  const filtered = players.filter(p => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (p.name || "").toLowerCase().includes(s) ||
           (p.fanta_id || "").toString().includes(s) ||
           teamName(p.team_id).toLowerCase().includes(s);
  });

  const stats = {
    total: players.length,
    with_fid: players.filter(p => p.fanta_id).length,
    without_fid: players.filter(p => !p.fanta_id).length,
    by_team: teams.map(t => ({ name: t.name, count: players.filter(p => p.team_id === t.team_id).length })),
    svincolati: players.filter(p => !p.team_id || p.status === "svincolato").length,
  };

  return (
    <div className="rounded-3xl border border-slate-700 bg-slate-900/60 p-5" data-testid="players-list-inline">
      <button onClick={() => setOpen(!open)} data-testid="toggle-players-list"
        className="w-full flex items-center justify-between text-left">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-cyan-400"/>
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-200">👀 Vedi Giocatori DB {players.length ? `(${players.length})` : ""}</h3>
        </div>
        <span className="text-xs text-slate-400">{open ? "▲ Chiudi" : "▼ Apri"}</span>
      </button>
      {open && (
        <div className="mt-4 space-y-3">
          {loading && <div className="text-center text-slate-400 py-4">Caricamento…</div>}
          {!loading && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-xs">
                <div className="bg-slate-800/60 rounded-lg p-2"><div className="font-mono font-black text-lg text-white">{stats.total}</div><div className="text-slate-500 text-[10px] uppercase">Totale</div></div>
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2"><div className="font-mono font-black text-lg text-emerald-300">{stats.with_fid}</div><div className="text-emerald-400 text-[10px] uppercase">Con fanta_id</div></div>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2"><div className="font-mono font-black text-lg text-amber-300">{stats.without_fid}</div><div className="text-amber-400 text-[10px] uppercase">Senza fanta_id</div></div>
                <div className="bg-rose-500/10 border border-rose-500/30 rounded-lg p-2"><div className="font-mono font-black text-lg text-rose-300">{stats.svincolati}</div><div className="text-rose-400 text-[10px] uppercase">Svincolati</div></div>
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2"><div className="font-mono font-black text-lg text-cyan-300">{teams.length}</div><div className="text-cyan-400 text-[10px] uppercase">Team</div></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-1 text-xs">
                {stats.by_team.map((t, i) => (
                  <div key={i} className="bg-slate-800/40 rounded px-2 py-1 flex justify-between">
                    <span className="text-slate-300 truncate">{t.name}</span>
                    <span className={`font-mono font-bold ${t.count === 0 ? "text-rose-400" : "text-emerald-400"}`}>{t.count}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 cerca per nome, fanta_id o team..."
                  data-testid="players-search"
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500"/>
                <button onClick={load} data-testid="refresh-players-btn"
                  className="px-3 py-2 rounded-lg bg-cyan-500/20 border border-cyan-500/50 text-cyan-200 text-xs font-bold hover:bg-cyan-500/30">
                  🔄 Refresh
                </button>
              </div>
              <div className="max-h-96 overflow-y-auto border border-slate-800 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-slate-800 sticky top-0">
                    <tr className="text-slate-400 uppercase tracking-wider">
                      <th className="px-2 py-2 text-left">Nome</th>
                      <th className="px-2 py-2 text-left">Fanta ID</th>
                      <th className="px-2 py-2 text-left">Ruolo</th>
                      <th className="px-2 py-2 text-left">Team</th>
                      <th className="px-2 py-2 text-right">Prezzo</th>
                      <th className="px-2 py-2 text-right">QA</th>
                      <th className="px-2 py-2 text-left">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.slice(0, 500).map((p, i) => (
                      <tr key={p.player_id} className={`border-t border-slate-800 ${i % 2 === 0 ? "bg-slate-950/40" : ""}`}>
                        <td className="px-2 py-1.5 text-white font-medium">{p.name}</td>
                        <td className="px-2 py-1.5 font-mono text-cyan-300">{p.fanta_id || "—"}</td>
                        <td className="px-2 py-1.5 text-slate-300">{p.role || "?"}</td>
                        <td className="px-2 py-1.5 text-slate-300">{teamName(p.team_id)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-amber-300">{p.prezzo_pagato ?? 0}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-300">{p.quotazione_attuale ?? 0}</td>
                        <td className="px-2 py-1.5 text-slate-400">{p.status || "attivo"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filtered.length > 500 && (
                  <div className="text-center text-xs text-slate-500 py-2 italic">... e altri {filtered.length - 500} giocatori (usa la ricerca per filtrare)</div>
                )}
                {filtered.length === 0 && (
                  <div className="text-center text-slate-500 py-6 italic">Nessun giocatore trovato.</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════
// Card sezione riutilizzabile
// ═══════════════════════════════════════════════════════════════════
function ImportCard({ icon, color, title, subtitle, desc, accept, children, extraActions }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const done = () => { setFile(null); };
  const colorMap = {
    cyan: "border-cyan-500/40 from-cyan-950/30",
    emerald: "border-emerald-500/40 from-emerald-950/30",
    amber: "border-amber-500/40 from-amber-950/30",
  };
  return (
    <div className={`rounded-3xl border ${colorMap[color]} bg-gradient-to-br to-slate-950 p-5 shadow-2xl shadow-black/40`}>
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="text-3xl">{icon}</div>
          <div>
            <h2 className="text-xl font-black text-slate-100">{title}</h2>
            <div className="text-xs text-slate-400 mt-0.5">{subtitle}</div>
          </div>
        </div>
        {extraActions}
      </div>
      <p className="text-xs text-slate-400 leading-relaxed mb-3 pl-1 border-l-2 border-slate-800/60 pl-2">{desc}</p>
      <input type="file" accept={accept} onChange={e => setFile(e.target.files?.[0] || null)}
        className="w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-slate-800 file:text-slate-200 file:font-bold hover:file:bg-slate-700 mb-3"
        data-testid={`file-input-${color}`}/>
      {file && <div className="text-[11px] text-slate-500 mb-2">📎 {file.name}</div>}
      <div className={loading ? "opacity-60 pointer-events-none" : ""}>
        {children(file, setLoading, done, loading)}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold uppercase tracking-wider border-b-2 -mb-px ${
        active ? "border-amber-400 text-amber-300" : "border-transparent text-slate-400 hover:text-white"
      }`}>
      {icon} {label}
    </button>
  );
}
function ModeBtn({ active, onClick, emoji, label, desc, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left rounded-md border p-3 transition-colors ${
        active ? "border-amber-400 bg-amber-400/10" : "border-slate-700 hover:border-slate-500"
      }`}>
      <div className="text-lg">{emoji}</div>
      <div className="font-bold text-sm mt-1">{label}</div>
      <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
    </button>
  );
}
function ResultBox({ label, v, emoji, color }) {
  const cls = color === "emerald" ? "text-emerald-400" : color === "amber" ? "text-amber-400" : "text-white";
  return (
    <div className="rounded-md bg-slate-900/60 border border-slate-800 p-3">
      <div className="text-2xl">{emoji}</div>
      <div className={`text-xl font-mono font-black ${cls}`}>{v ?? 0}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
    </div>
  );
}

function DiffImport({ league }) {
  const { teams } = useLeague();
  const [step, setStep] = useState(1); // 1: upload, 2: review/mapping, 3: confirm/apply
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);
  const [mapping, setMapping] = useState({}); // { unmatched_slug: team_id }

  const slugify = (s) => (s || "").toLowerCase().normalize("NFKD").replace(/[^a-z0-9]+/g, "");
  const unmatched = preview?.summary?.squadre_non_matchate || [];

  const runDiff = async (goToStep) => {
    if (!file) { toast.error("Seleziona un file"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Build mapping payload {slug: team_id}
      const mapping_payload = {};
      for (const [name, tid] of Object.entries(mapping)) {
        if (tid) mapping_payload[slugify(name)] = tid;
      }
      if (Object.keys(mapping_payload).length) {
        fd.append("team_mapping", JSON.stringify(mapping_payload));
      }
      const r = await api.post(`/leagues/${league.league_id}/imports/roster-diff?apply=false`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setPreview(r.data);
      if (goToStep) setStep(goToStep);
      toast.success(`📊 ${r.data.summary.trasferimenti} trasferimenti · ${r.data.summary.arrivati} arrivati · ${r.data.summary.partiti} partiti`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const doApply = async () => {
    if (!file) return;
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const mapping_payload = {};
      for (const [name, tid] of Object.entries(mapping)) {
        if (tid) mapping_payload[slugify(name)] = tid;
      }
      if (Object.keys(mapping_payload).length) {
        fd.append("team_mapping", JSON.stringify(mapping_payload));
      }
      const r = await api.post(`/leagues/${league.league_id}/imports/roster-diff?apply=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setApplied(r.data);
      toast.success(`✅ ${r.data.summary.trasferimenti} trasferimenti applicati`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  const resetWizard = () => {
    setStep(1); setFile(null); setPreview(null); setApplied(null); setMapping({});
  };

  // Auto-recompute preview when mapping changes and we're on step 2
  const remapAndReload = async () => { await runDiff(null); };

  const allUnmatchedResolved = unmatched.every(u => mapping[u]);

  return (
    <div className="space-y-4" data-testid="import-wizard">
      {/* Header + Stepper */}
      <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-5 shadow-xl shadow-black/40">
        <div className="border-l-4 border-purple-400 pl-3 mb-4">
          <h3 className="font-black text-lg">🧙 Wizard Import Rose</h3>
          <p className="text-xs text-slate-400 mt-1">Import guidato in 3 step con anteprima diff, mapping club e conferma finale.</p>
        </div>
        <Stepper current={step} steps={[
          { n: 1, label: "Upload", emoji: "📎" },
          { n: 2, label: "Preview & Mapping", emoji: "🔍" },
          { n: 3, label: "Conferma", emoji: "✅" },
        ]} />
      </div>

      {/* STEP 1: Upload */}
      {step === 1 && (
        <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 space-y-4 shadow-xl shadow-black/40">
          <div className="text-xs text-slate-300 leading-relaxed border-l-2 border-emerald-400/40 pl-3 bg-slate-950/40 py-2 rounded-r-xl">
            💡 Formato atteso (case-insensitive): <code className="text-amber-300">Squadra</code>, <code className="text-amber-300">Nome</code> (opz. <code className="text-amber-300">Ruolo</code>, <code className="text-amber-300">Costo</code>, <code className="text-amber-300">Club</code>). Le squadre nel file vengono matchate con quelle in lega tramite normalizzazione del nome.
          </div>
          <div>
            <label className="text-xs text-slate-400 uppercase font-bold block mb-2">📎 File nuove rose (.xlsx/.xls/.csv)</label>
            <div className="border border-dashed border-slate-700 rounded-2xl p-6 text-center hover:border-purple-500 transition-colors">
              <input type="file" accept=".xlsx,.xls,.csv" onChange={e=>{ setFile(e.target.files?.[0]); setPreview(null); }} className="hidden" id="diff-file" data-testid="diff-file"/>
              <label htmlFor="diff-file" className="cursor-pointer inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
                <Upload size={16}/> {file ? file.name : "Scegli file..."}
              </label>
            </div>
          </div>
          <button onClick={()=>runDiff(2)} disabled={loading || !file} data-testid="wizard-next-step2-btn"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 hover:from-purple-400 hover:to-purple-600 text-white font-bold uppercase tracking-wider disabled:opacity-50 shadow-lg shadow-purple-500/30">
            {loading ? "Analizzo…" : <>Prossimo <ArrowRight size={16}/></>}
          </button>
        </div>
      )}

      {/* STEP 2: Preview + Mapping */}
      {step === 2 && preview && (
        <div className="space-y-4">
          {/* KPI summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <DiffKpi label="Trasferimenti" value={preview.summary.trasferimenti} emoji="🔀" color="purple"/>
            <DiffKpi label="Arrivati" value={preview.summary.arrivati} emoji="⭐" color="emerald"/>
            <DiffKpi label="Partiti" value={preview.summary.partiti} emoji="✈️" color="rose"/>
            <DiffKpi label="Invariati" value={preview.summary.invariati} emoji="✅" color="slate"/>
          </div>

          {/* Team Mapping */}
          {unmatched.length > 0 && (
            <div className="rounded-3xl border border-amber-500/40 bg-amber-500/10 p-5 space-y-3" data-testid="team-mapping-panel">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-300 mt-0.5"/>
                <div>
                  <div className="font-black text-amber-300 uppercase tracking-widest text-sm">Mapping Club necessario</div>
                  <div className="text-xs text-slate-300 mt-1">
                    {unmatched.length} squadr{unmatched.length===1?'a':'e'} nel file non ha{unmatched.length===1?'':'nno'} un match automatico. Associa manualmente ciascuna a una squadra della lega. Le squadre non mappate verranno ignorate.
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {unmatched.map(name => (
                  <div key={name} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 bg-slate-950/40 border border-slate-800 rounded-xl p-2">
                    <div className="flex-1 min-w-0 text-sm">
                      <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">Dal file</div>
                      <div className="text-slate-100 font-bold truncate">{name}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-500 self-center hidden sm:block"/>
                    <div className="flex-1">
                      <select
                        value={mapping[name] || ""}
                        onChange={e => setMapping(m => ({ ...m, [name]: e.target.value }))}
                        data-testid={`map-select-${slugify(name)}`}
                        className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100">
                        <option value="">— Ignora (non mappare) —</option>
                        {teams.map(t => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={remapAndReload} disabled={loading}
                data-testid="apply-mapping-btn"
                className="w-full text-sm font-bold uppercase tracking-wider px-4 py-2 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 disabled:opacity-50">
                {loading ? "Ricalcolo…" : "🔄 Ricalcola con mapping"}
              </button>
            </div>
          )}

          {/* Detail lists */}
          <div className="grid md:grid-cols-2 gap-3">
            <DiffList title="🔀 Trasferimenti (verranno applicati)" items={preview.trasferimenti} color="purple" render={t => (
              <div className="text-sm">
                <div className="font-bold flex items-center gap-2">
                  <span>{t.player_name}</span>
                  {t.role && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400">{t.role}</span>}
                  {t.prezzo != null && <span className="ml-auto text-xs font-mono text-amber-300">{t.prezzo} cr</span>}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  <span className="text-rose-300">{t.from_team_name}</span>
                  <span className="mx-1 text-slate-600">→</span>
                  <span className="text-emerald-300">{t.to_team_name}</span>
                </div>
              </div>
            )}/>
            <DiffList title="⭐ Arrivati (info, non applicati)" items={preview.arrivati} color="emerald" render={a => (
              <div className="text-sm">
                <div className="font-bold flex items-center gap-2">
                  <span>{a.player_name}</span>
                  {a.role && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400">{a.role}</span>}
                  {a.prezzo != null && <span className="ml-auto text-xs font-mono text-amber-300">{a.prezzo} cr</span>}
                </div>
                <div className="text-xs text-emerald-300 mt-0.5">→ {a.to_team_name}</div>
              </div>
            )}/>
            <DiffList title="✈️ Partiti (info, non applicati)" items={preview.partiti} color="rose" render={p => (
              <div className="text-sm">
                <div className="font-bold flex items-center gap-2">
                  <span>{p.player_name}</span>
                  {p.role && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-slate-800 border border-slate-700 text-slate-400">{p.role}</span>}
                </div>
                <div className="text-xs text-rose-300 mt-0.5">da {p.from_team_name}</div>
              </div>
            )}/>
          </div>

          {/* Nav */}
          <div className="flex gap-2">
            <button onClick={()=>setStep(1)} data-testid="wizard-back-step1-btn"
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2">
              <ArrowLeft size={14}/> Indietro
            </button>
            <button
              onClick={()=>setStep(3)}
              disabled={unmatched.length > 0 && !allUnmatchedResolved}
              data-testid="wizard-next-step3-btn"
              title={unmatched.length > 0 && !allUnmatchedResolved ? "Risolvi il mapping club prima di procedere" : ""}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 hover:from-purple-400 hover:to-purple-600 text-white font-bold uppercase text-sm tracking-wider disabled:opacity-50 shadow-lg shadow-purple-500/30">
              Vai alla conferma <ArrowRight size={14}/>
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Confirm & Apply */}
      {step === 3 && preview && !applied && (
        <div className="border border-emerald-500/40 rounded-3xl bg-emerald-500/10 p-6 space-y-4 shadow-xl shadow-emerald-500/10">
          <div className="text-lg font-black text-emerald-300 uppercase tracking-wider flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5"/> Conferma applicazione
          </div>
          <div className="text-sm text-slate-300">
            Stai per applicare <strong className="text-amber-300">{preview.trasferimenti?.length || 0} trasferimenti</strong> tra squadre della lega. I giocatori verranno spostati e verrà creata una operation &quot;trasferimento_manuale&quot; per ognuno.
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-slate-950/40 border border-slate-800 rounded-xl p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Verranno applicati</div>
              <div className="text-2xl font-black text-emerald-300">{preview.trasferimenti?.length || 0}</div>
              <div className="text-[10px] text-slate-400">trasferimenti interni</div>
            </div>
            <div className="bg-slate-950/40 border border-slate-800 rounded-xl p-3">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Non applicati automaticamente</div>
              <div className="text-2xl font-black text-slate-400">{(preview.arrivati?.length || 0) + (preview.partiti?.length || 0)}</div>
              <div className="text-[10px] text-slate-400">arrivati + partiti (serve gestione manuale)</div>
            </div>
          </div>
          {unmatched.length > 0 && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
              ⚠️ {unmatched.length} squadre non mappate verranno ignorate. Torna indietro se vuoi risolverle.
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={()=>setStep(2)} disabled={applying} data-testid="wizard-back-step2-btn"
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50">
              <ArrowLeft size={14}/> Indietro
            </button>
            <button onClick={doApply} disabled={applying || (preview.trasferimenti?.length || 0) === 0}
              data-testid="wizard-apply-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-br from-emerald-400 to-emerald-600 hover:from-emerald-300 hover:to-emerald-500 text-slate-950 font-black uppercase tracking-wider disabled:opacity-50 shadow-lg shadow-emerald-500/40">
              {applying ? "Applico…" : "✅ APPLICA TRASFERIMENTI"}
            </button>
          </div>
        </div>
      )}

      {/* Result: applied */}
      {applied && (
        <div className="border border-emerald-500/40 rounded-3xl bg-emerald-500/10 p-6 space-y-4 shadow-xl shadow-emerald-500/10" data-testid="wizard-applied-result">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-8 h-8 text-emerald-300"/>
            <div>
              <div className="font-black text-emerald-300 uppercase tracking-wider text-lg">Import completato!</div>
              <div className="text-sm text-slate-300">{applied.summary?.trasferimenti || 0} trasferimenti applicati con successo.</div>
            </div>
          </div>
          <button onClick={resetWizard} data-testid="wizard-restart-btn"
            className="w-full px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-bold uppercase tracking-wider">
            🔄 Nuovo import
          </button>
        </div>
      )}
    </div>
  );
}

function Stepper({ current, steps }) {
  return (
    <div className="flex items-center gap-1" data-testid="wizard-stepper">
      {steps.map((s, i) => {
        const active = s.n === current;
        const done = s.n < current;
        return (
          <div key={s.n} className="flex-1 flex items-center gap-1">
            <div className={`flex-1 flex items-center gap-2 rounded-xl border px-3 py-2 transition-colors ${
              active ? "border-purple-400 bg-purple-500/15 text-purple-100"
              : done ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
              : "border-slate-700 bg-slate-900/40 text-slate-500"
            }`} data-testid={`stepper-${s.n}${active ? "-active" : done ? "-done" : ""}`}>
              <span className="text-lg">{done ? "✓" : s.emoji}</span>
              <div className="min-w-0">
                <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">Step {s.n}</div>
                <div className="text-xs font-bold truncate">{s.label}</div>
              </div>
            </div>
            {i < steps.length - 1 && <div className={`w-2 h-0.5 ${done ? "bg-emerald-400" : "bg-slate-700"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function DiffKpi({ label, value, emoji, color }) {
  const colorMap = {
    purple: "border-purple-500/40 bg-purple-500/10 text-purple-300",
    emerald: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300",
    rose: "border-rose-500/40 bg-rose-500/10 text-rose-300",
    slate: "border-slate-700 bg-slate-800/40 text-slate-300",
  }[color] || "";
  return (
    <div className={`rounded-3xl border ${colorMap} p-4 shadow-xl shadow-black/30`}>
      <div className="text-lg">{emoji}</div>
      <div className="text-3xl font-mono font-black mt-1 text-white">{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-bold">{label}</div>
    </div>
  );
}

function DiffList({ title, items, color, render }) {
  const colorMap = {
    purple: "border-purple-500/30",
    emerald: "border-emerald-500/30",
    rose: "border-rose-500/30",
  }[color] || "border-slate-800";
  return (
    <div className={`rounded-3xl border ${colorMap} bg-gradient-to-br from-slate-900 to-slate-950 shadow-xl shadow-black/40 overflow-hidden`}>
      <div className="p-3 border-b border-slate-800 flex items-center justify-between">
        <h4 className="font-black uppercase text-xs tracking-wider">{title}</h4>
        <span className="text-[10px] text-slate-500">{items?.length || 0}</span>
      </div>
      {items?.length === 0 ? (
        <div className="p-6 text-center text-slate-500 text-xs">Nessuno</div>
      ) : (
        <ol className="divide-y divide-slate-800 max-h-72 overflow-y-auto">
          {items.map((item, i) => (
            <li key={i} className="p-3 hover:bg-slate-800/40 transition-colors">{render(item)}</li>
          ))}
        </ol>
      )}
    </div>
  );
}


function PdfImport({ league }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) { toast.error("Seleziona un PDF"); return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/leagues/${league.league_id}/imports/fantacalcio-pdf?apply=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setResult(r.data);
      toast.success(`✅ ${r.data.updated} giocatori aggiornati con FantaMedia/MediaVoto`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 space-y-4 shadow-xl shadow-black/40">
        <div className="border-l-4 border-blue-400 pl-3">
          <h3 className="font-black text-lg">📄 Import PDF Fantacalcio</h3>
          <p className="text-sm text-slate-400 mt-1">
            Carica il <strong>PDF ufficiale del Fantacalcio</strong> (Listone o Statistiche): estrae automaticamente <strong className="text-emerald-300">Fanta Media</strong> e <strong className="text-emerald-300">Media Voto</strong> per ogni giocatore e li applica alla tua lega.
          </p>
        </div>

        <div className="text-xs text-slate-300 leading-relaxed border-l-2 border-blue-400/40 pl-3 bg-slate-950/40 py-2 rounded-r-xl">
          💡 Formato riconosciuto: righe con <code className="text-amber-300">R Nome Squadra MV FM Presenze</code> (formato standard Fantacalcio.it).
        </div>

        <div>
          <label className="text-xs text-slate-400 uppercase font-bold block mb-2">📎 File PDF</label>
          <div className="border border-dashed border-slate-700 rounded-2xl p-6 text-center hover:border-blue-500 transition-colors">
            <input type="file" accept=".pdf" onChange={e=>setFile(e.target.files?.[0])} className="hidden" id="pdf-file" data-testid="pdf-file"/>
            <label htmlFor="pdf-file" className="cursor-pointer inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white">
              <Upload size={16}/> {file ? file.name : "Scegli PDF..."}
            </label>
          </div>
        </div>

        <button onClick={upload} disabled={loading || !file} data-testid="pdf-upload-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 hover:from-blue-400 hover:to-blue-600 text-white font-bold uppercase tracking-wider disabled:opacity-50 shadow-lg shadow-blue-500/30">
          {loading ? "Estraggo…" : "🚀 Estrai e applica valutazioni"}
        </button>
      </div>

      {result && (
        <div className="border border-emerald-500/40 rounded-3xl bg-emerald-500/10 p-6 shadow-xl shadow-emerald-500/10" data-testid="pdf-result">
          <div className="font-bold text-emerald-300 mb-3">✅ Import completato</div>
          <div className="grid grid-cols-3 gap-3">
            <ResultBox emoji="📊" label="Righe estratte" v={result.parsed}/>
            <ResultBox emoji="✨" label="Aggiornati" v={result.updated} color="emerald"/>
            <ResultBox emoji="❓" label="Non trovati" v={result.not_found_count} color="amber"/>
          </div>
          {result.not_found_sample?.length > 0 && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-amber-300">Vedi giocatori non trovati</summary>
              <div className="mt-2 text-slate-400 text-xs font-mono">{result.not_found_sample.join(", ")}</div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// RinnoviImport (Fase 8/11): import totale del foglio "Rinnovi" formato Magnati.
// Aggiorna: ruoli, squadra reale, prezzo pagato, Q.I., Q.A., stipendi, svincoli.
// ---------------------------------------------------------------------------
function RinnoviImport({ league }) {
  const [file, setFile] = useState(null);
  const [applySvincoli, setApplySvincoli] = useState(true);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const phase = Number(league?.season_phase ?? 3);
  const gateOk = phase === 8 || phase === 11;

  const submit = async () => {
    if (!file) { toast.error("Seleziona un file Excel"); return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/leagues/${league.league_id}/imports/rinnovi?apply_svincoli=${applySvincoli}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setResult(r.data);
      toast.success(`✅ ${r.data.updated} giocatori aggiornati · ${r.data.svincolati} svincoli`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className={`border rounded-3xl p-6 space-y-4 shadow-xl ${gateOk
        ? "border-emerald-500/40 bg-gradient-to-br from-emerald-500/5 to-slate-950 shadow-emerald-500/10"
        : "border-amber-500/40 bg-gradient-to-br from-amber-500/5 to-slate-950 shadow-amber-500/10"}`}>
        <div className="text-sm text-slate-300 leading-relaxed border-l-2 border-emerald-400/60 pl-3">
          🔁 <strong className="text-emerald-300">Import totale foglio Rinnovi</strong> — formato Excel Lega dei Magnati (header a riga 3).
          Aggiorna in blocco: <em className="text-amber-300">ruoli, squadra reale, prezzo pagato, Q.I., Q.A., stipendio (colonna L), svincoli con crediti recuperati</em>.
          Storicizza Q.A. e stipendi.
        </div>

        <div className={`rounded-md px-3 py-2 text-xs ${gateOk
          ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300"
          : "bg-amber-500/10 border border-amber-500/40 text-amber-300"}`}>
          {gateOk
            ? <>🟢 <strong>Fase {phase}</strong> — Import consentito.</>
            : <>🔒 <strong>Fase {phase}</strong> — L&apos;import Rinnovi è disponibile solo in <strong>Fase 8 (Apertura Rinnovi)</strong> o <strong>Fase 11 (Chiusura Economica)</strong>.</>
          }
        </div>

        <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={applySvincoli} onChange={e=>setApplySvincoli(e.target.checked)}
            data-testid="rinnovi-apply-svincoli"
            className="w-4 h-4 accent-emerald-400"/>
          <span>Applica anche gli <strong className="text-rose-300">svincoli</strong> (Svincola=True) con restituzione crediti al team</span>
        </label>

        <div>
          <label className="text-xs text-slate-400 uppercase font-bold block mb-2">📎 File Excel (.xlsx) con foglio Rinnovi</label>
          <div className="border border-dashed border-slate-700 rounded-lg p-6 text-center hover:border-emerald-500 transition-colors">
            <input type="file" accept=".xlsx,.xls" onChange={e=>setFile(e.target.files?.[0])}
              className="hidden" id="rinnovi-file-input" data-testid="rinnovi-file" />
            <label htmlFor="rinnovi-file-input" className="cursor-pointer">
              <FileSpreadsheet size={32} className="mx-auto text-slate-500 mb-2"/>
              <div className="font-bold text-emerald-400">{file ? file.name : "Clicca per scegliere un file"}</div>
              <div className="text-xs text-slate-500 mt-1">{file ? `${(file.size/1024).toFixed(1)} KB` : "Es. Pre asta furti Fanta Lega dei Magnati 26:27.xlsx"}</div>
            </label>
          </div>
        </div>

        <button onClick={submit} disabled={loading || !file || !gateOk}
          data-testid="rinnovi-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-emerald-400 hover:bg-emerald-300 text-slate-950 font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed">
          <Upload size={16}/> {loading ? "Import in corso…" : "🚀 Importa Rinnovi"}
        </button>
      </div>

      {result && (
        <div className="border border-emerald-500/40 rounded-3xl bg-emerald-500/10 p-6 shadow-xl shadow-emerald-500/10" data-testid="rinnovi-result">
          <div className="font-bold text-emerald-300 mb-3">✅ Import Rinnovi completato</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-center">
            <ResultBox label="Righe file" v={result.total_rows} emoji="📊"/>
            <ResultBox label="Match trovati" v={result.matched} emoji="🎯"/>
            <ResultBox label="Aggiornati" v={result.updated} emoji="✨" color="emerald"/>
            <ResultBox label="Svincolati" v={result.svincolati} emoji="🔓" color="amber"/>
            <ResultBox label="Non trovati" v={result.non_trovati_count} emoji="❓" color="amber"/>
          </div>
          {result.changes_sample?.length > 0 && (
            <details className="mt-4 text-sm" open>
              <summary className="cursor-pointer text-emerald-300 font-bold">🔍 Modifiche (sample)</summary>
              <div className="mt-2 space-y-1 text-xs font-mono">
                {result.changes_sample.map((c, i) => (
                  <div key={i} className="flex gap-2">
                    <span className="text-slate-300">{c.name}</span>
                    <span className="text-slate-500">→</span>
                    <span className="text-emerald-300">{c.changes.join(", ")}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
          {result.non_trovati_sample?.length > 0 && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-amber-300 font-bold">⚠️ Giocatori non trovati (top 20)</summary>
              <div className="mt-2 text-slate-400 text-xs font-mono">{result.non_trovati_sample.join(", ")}</div>
            </details>
          )}
          {result.ambigui?.length > 0 && (
            <details className="mt-3 text-sm">
              <summary className="cursor-pointer text-rose-300 font-bold">🔀 Ambigui (nome multipli)</summary>
              <div className="mt-2 text-slate-400 text-xs font-mono">{result.ambigui.map(a=>`${a.name} (${a.candidates})`).join(", ")}</div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}



// ═══════════════════════════════════════════════════════════════════
// ITER 61: IMPORT ROSE FANTACALCIO.IT (CSV) + POST-CALENDARIO (XLSX)
// ═══════════════════════════════════════════════════════════════════

function RoseFantacalcioImport({ league }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const doPreview = async () => {
    if (!file) { toast.error("Seleziona un file CSV"); return; }
    setLoading(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/leagues/${league.league_id}/imports/rose-fantacalcio?preview=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`✅ Anteprima: ${r.data.total_teams_matched}/${r.data.total_teams_in_file} squadre matchate`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const doApply = async () => {
    if (!file) return;
    if (!confirm("Applicare le proprietà dal file rose Fantacalcio.it?\n\n⚠️ Verrà creato uno snapshot pre-import automatico.")) return;
    setApplying(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/leagues/${league.league_id}/imports/rose-fantacalcio?preview=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`✅ ${r.data.applied_transfers} trasferimenti applicati. Snapshot: ${r.data.snapshot_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 space-y-4">
      <div className="text-sm text-slate-300 leading-relaxed border-l-2 border-cyan-400/40 pl-3">
        🌐 <strong className="text-cyan-300">Rose ufficiali Fantacalcio.it</strong> — CSV export della lega privata.<br/>
        Ricostruisce la <strong className="text-emerald-300">FOTOGRAFIA DELLA PROPRIETÀ</strong> (chi possiede chi).<br/>
        <span className="text-amber-300">⚠️ NON modifica quotazioni.</span> I giocatori assenti dal file <strong>restano in rosa</strong> (marchiati per revisione).
      </div>
      <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0])} className="w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-cyan-500/20 file:text-cyan-300 file:font-bold hover:file:bg-cyan-500/30" data-testid="rose-fanta-file"/>
      <div className="flex gap-2 flex-wrap">
        <button onClick={doPreview} disabled={!file || loading} className="px-4 py-2 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 font-bold text-sm disabled:opacity-50" data-testid="rose-fanta-preview-btn">
          {loading ? "..." : "🔍 Anteprima"}
        </button>
        <button onClick={doApply} disabled={!file || !preview || applying} className="px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 font-bold text-sm disabled:opacity-50" data-testid="rose-fanta-apply-btn">
          {applying ? "..." : "✅ Applica"}
        </button>
      </div>
      {preview && (
        <div className="space-y-3 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatCard label="Confermati" v={preview.summary.confirmed} color="emerald"/>
            <StatCard label="Trasf. interni" v={preview.summary.transferred_internal} color="cyan"/>
            <StatCard label="Nuovi arrivati" v={preview.summary.new_arrivals} color="amber"/>
            <StatCard label="Missing dal file" v={preview.summary.missing_from_file} color="rose"/>
            <StatCard label="Fuori listone" v={preview.summary.fuori_listone} color="orange"/>
            <StatCard label="Unresolved" v={preview.summary.unresolved_names} color="slate"/>
            <StatCard label="Squadre match" v={`${preview.total_teams_matched}/${preview.total_teams_in_file}`} color="indigo"/>
            {preview.applied_transfers !== undefined && (<StatCard label="Applicati" v={preview.applied_transfers} color="emerald"/>)}
          </div>
          {preview.unmatched_teams?.length > 0 && (
            <div className="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200">
              <strong>⚠️ Squadre non riconosciute:</strong> {preview.unmatched_teams.join(", ")}
            </div>
          )}
          {preview.fuori_listone?.length > 0 && (
            <div className="p-3 rounded-2xl bg-orange-500/10 border border-orange-500/30 text-sm">
              <div className="font-bold text-orange-300 mb-2">🚫 Giocatori fuori listone ma di proprietà:</div>
              <ul className="text-orange-200 space-y-0.5">
                {preview.fuori_listone.slice(0,20).map((f,i) => (
                  <li key={i}>• <strong>{f.name}</strong> — {f.team} <span className="text-orange-400 text-xs">[{f.status}]</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PostCalendarioImport({ league }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [rosePreview, setRosePreview] = useState(null); // Iter 62: preview rose per confronto
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [conflictOpen, setConflictOpen] = useState(false);

  // Iter 62: rileva conflitti di costo tra post-calendario e file rose
  const costConflicts = (() => {
    if (!preview || !rosePreview) return [];
    const roseCosts = {};
    (rosePreview.teams || []).forEach(t => {
      (t.players || []).forEach(p => {
        if (p.db_player_id && p.costo_file != null) {
          roseCosts[p.db_player_id] = { cost: p.costo_file, name: p.name };
        }
      });
    });
    const conflicts = [];
    (preview.sheets || []).forEach(s => {
      (s.players || []).forEach(p => {
        if (p.db_player_id && p.costo_file != null) {
          const rose = roseCosts[p.db_player_id];
          if (rose && rose.cost !== p.costo_file) {
            conflicts.push({
              player_id: p.db_player_id, name: p.name,
              rose_cost: rose.cost, post_cost: p.costo_file, team_name: s.team_name,
              db_cost: p.prezzo_pagato_db,
            });
          }
        }
      });
    });
    return conflicts;
  })();

  const doPreview = async () => {
    if (!file) { toast.error("Seleziona un file XLSX"); return; }
    setLoading(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/leagues/${league.league_id}/imports/post-calendario?preview=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      toast.success(`✅ Anteprima: ${r.data.summary.cost_updates} aggiornamenti costi rilevati`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const doApply = async () => {
    if (!file) return;
    // Iter 62: se ci sono conflitti, apri dialog invece di applicare direttamente
    if (costConflicts.length > 0 && !conflictOpen) {
      setConflictOpen(true);
      return;
    }
    if (!confirm("⚠️ La colonna \"Costo d'acquisto\" è AUTORITATIVA: sovrascriverà i prezzi_pagato dei giocatori.\n\nProcedere?")) return;
    setApplying(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post(`/leagues/${league.league_id}/imports/post-calendario?preview=false`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      setConflictOpen(false);
      toast.success(`✅ ${r.data.applied_updates} aggiornamenti applicati`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  return (
    <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 p-6 space-y-4">
      <div className="text-sm text-slate-300 leading-relaxed border-l-2 border-amber-400/40 pl-3">
        📅 <strong className="text-amber-300">Post-Calendario Gestionale</strong> — foglio Rinnovi con TUTTE le squadre concatenate.<br/>
        La colonna <strong className="text-emerald-300">Costo d&apos;acquisto</strong> è <strong>AUTORITATIVA</strong> per prezzo_pagato.<br/>
        Aggiorna anche stipendio e merchandising.
      </div>
      <input type="file" accept=".xlsx,.xls" onChange={e => setFile(e.target.files?.[0])} className="w-full text-sm text-slate-300 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-amber-500/20 file:text-amber-300 file:font-bold hover:file:bg-amber-500/30" data-testid="post-cal-file"/>
      <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
        <span>Opzionale: carica anche il file rose Fantacalcio.it per rilevare conflitti di costo:</span>
        <input type="file" accept=".csv" onChange={async e => {
          const rf = e.target.files?.[0];
          if (!rf) return;
          try {
            const fd = new FormData(); fd.append("file", rf);
            const r = await api.post(`/leagues/${league.league_id}/imports/rose-fantacalcio?preview=true`, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setRosePreview(r.data);
            toast.success("File rose caricato per confronto conflitti");
          } catch (err) { toast.error(formatApiError(err)); }
        }} className="text-xs text-slate-400 file:text-[10px] file:py-1 file:px-2 file:rounded file:border-0 file:bg-slate-800 file:text-cyan-300" data-testid="post-cal-rose-compare-file"/>
        {rosePreview && <span className="text-cyan-300">✓ rose caricato ({rosePreview.total_teams_matched} team)</span>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button onClick={doPreview} disabled={!file || loading} className="px-4 py-2 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40 hover:bg-amber-500/30 font-bold text-sm disabled:opacity-50" data-testid="post-cal-preview-btn">
          {loading ? "..." : "🔍 Anteprima"}
        </button>
        <button onClick={doApply} disabled={!file || !preview || applying} className="px-4 py-2 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 font-bold text-sm disabled:opacity-50" data-testid="post-cal-apply-btn">
          {applying ? "..." : costConflicts.length > 0 ? `⚠️ Applica (${costConflicts.length} conflitti)` : "✅ Applica"}
        </button>
      </div>

      {/* Iter 62: Popup conflitti costi */}
      {conflictOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={() => !applying && setConflictOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="w-full max-w-3xl max-h-[85vh] rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-amber-500/50 shadow-2xl flex flex-col" data-testid="conflict-modal">
            <div className="p-5 border-b border-slate-800">
              <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold">⚠️ Conflitti Costi Rilevati</div>
              <h3 className="text-2xl font-black mt-1">Il file post-calendario e le rose Fantacalcio non concordano</h3>
              <div className="text-xs text-slate-400 mt-2">
                <strong>{costConflicts.length}</strong> giocatori hanno un costo diverso nei due file.
                Come indicato nel piano, <strong className="text-amber-300">Post-Calendario prevale</strong> (colonna &quot;Costo d&apos;acquisto&quot; autoritativa).
                Verifica manualmente prima di confermare.
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <table className="w-full text-xs">
                <thead className="text-slate-500 uppercase tracking-widest text-[10px]">
                  <tr>
                    <th className="px-2 py-2 text-left">Giocatore</th>
                    <th className="px-2 py-2 text-left">Team</th>
                    <th className="px-2 py-2 text-right">DB</th>
                    <th className="px-2 py-2 text-right">Rose CSV</th>
                    <th className="px-2 py-2 text-right">Post-Cal ✓</th>
                  </tr>
                </thead>
                <tbody>
                  {costConflicts.map((c, i) => (
                    <tr key={i} className="border-t border-slate-800" data-testid={`conflict-row-${i}`}>
                      <td className="px-2 py-1.5 font-bold text-slate-200">{c.name}</td>
                      <td className="px-2 py-1.5 text-slate-400">{c.team_name}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-slate-400">{c.db_cost ?? "—"}cr</td>
                      <td className="px-2 py-1.5 text-right font-mono text-cyan-300">{c.rose_cost}cr</td>
                      <td className="px-2 py-1.5 text-right font-mono font-bold text-amber-300">{c.post_cost}cr</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-slate-800 flex items-center justify-end gap-2">
              <button onClick={() => setConflictOpen(false)} className="px-4 py-2 rounded-2xl border border-slate-700 text-slate-400 text-sm font-bold" data-testid="conflict-cancel-btn">Annulla</button>
              <button onClick={doApply} disabled={applying} className="px-4 py-2 rounded-2xl bg-amber-500/20 border border-amber-500/50 text-amber-200 font-black text-sm hover:bg-amber-500/30 disabled:opacity-50" data-testid="conflict-confirm-btn">
                {applying ? "Applico…" : "✅ Applica Post-Calendario"}
              </button>
            </div>
          </div>
        </div>
      )}
      {preview && (
        <div className="space-y-3 mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <StatCard label="Confermati" v={preview.summary.confirmed} color="emerald"/>
            <StatCard label="Cost updates" v={preview.summary.cost_updates} color="amber"/>
            <StatCard label="Nuovi/unmatched" v={preview.summary.new_or_unmatched} color="cyan"/>
            <StatCard label="Missing from file" v={preview.summary.missing_from_sheet} color="rose"/>
            {preview.applied_updates !== undefined && (<StatCard label="Applicati" v={preview.applied_updates} color="emerald"/>)}
          </div>
          {preview.unmatched_sheets?.length > 0 && (
            <div className="p-3 rounded-2xl bg-rose-500/10 border border-rose-500/30 text-sm text-rose-200">
              <strong>⚠️ Squadre non riconosciute:</strong> {preview.unmatched_sheets.join(", ")}
            </div>
          )}
          {/* Iter 62: blocchi orfani senza team header */}
          {preview.sheets?.filter(s => s.orphan_block).map((s, i) => (
            <div key={`orphan-${i}`} className="p-3 rounded-2xl bg-purple-500/10 border border-purple-500/40 text-sm" data-testid={`orphan-block-${i}`}>
              <div className="font-bold text-purple-300 mb-1">🏷️ Blocco senza team header rilevato ({s.players.length} giocatori)</div>
              {s.suggested_team_name ? (
                <div className="text-slate-300 text-xs">
                  Suggerimento automatico: <strong className="text-purple-200">{s.suggested_team_name}</strong> (confidence {Math.round((s.suggested_confidence || 0) * 100)}%).
                  L&apos;apply userà questo team come destinatario per i cost_update.
                </div>
              ) : (
                <div className="text-rose-300 text-xs">Impossibile inferire il team. Verifica il file: potrebbe mancare l&apos;header di una squadra (es. Chicago Hasbull).</div>
              )}
            </div>
          ))}
          <div className="grid grid-cols-1 gap-2">
            {preview.sheets?.map(s => (
              <details key={s.team_id} className="p-3 rounded-2xl bg-slate-950 border border-slate-800">
                <summary className="cursor-pointer font-bold text-slate-200 text-sm">
                  {s.team_name} <span className="text-xs text-slate-400 font-normal">— conf {s.summary.confirmed} · costUpd {s.summary.cost_updates} · new {s.summary.new} · miss {s.summary.missing}</span>
                </summary>
                <ul className="mt-2 space-y-0.5 text-xs text-slate-300 max-h-40 overflow-auto">
                  {s.players?.filter(p => p.action !== "confirmed").slice(0,30).map((p,i) => (
                    <li key={i} className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded font-mono text-[10px] ${p.action==="cost_update"?"bg-amber-500/20 text-amber-300":p.action==="missing_from_sheet"?"bg-rose-500/20 text-rose-300":"bg-cyan-500/20 text-cyan-300"}`}>{p.action}</span>
                      <span>{p.name}</span>
                      {p.action === "cost_update" && (<span className="text-slate-400"> {p.prezzo_pagato_db}cr → <strong className="text-emerald-300">{p.costo_file}cr</strong></span>)}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, v, color = "slate" }) {
  const cmap = {
    emerald: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
    cyan: "text-cyan-300 border-cyan-500/40 bg-cyan-500/10",
    amber: "text-amber-300 border-amber-500/40 bg-amber-500/10",
    rose: "text-rose-300 border-rose-500/40 bg-rose-500/10",
    orange: "text-orange-300 border-orange-500/40 bg-orange-500/10",
    slate: "text-slate-300 border-slate-700 bg-slate-800/30",
    indigo: "text-indigo-300 border-indigo-500/40 bg-indigo-500/10",
  };
  return (
    <div className={`p-2.5 rounded-2xl border ${cmap[color] || cmap.slate}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 font-bold">{label}</div>
      <div className="text-xl font-black font-mono">{v}</div>
    </div>
  );
}
